import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Sparkles,
  PlugZap,
  ListTree,
  LineChart,
  Check,
  ArrowRight,
  ShieldCheck,
  Server,
  Lock,
  FileText,
  Mail,
  MoonStar,
  CalendarClock,
  Clock,
  Inbox,
  Wallet,
} from "lucide-react";
import { RecrystaMark } from "@/components/RecrystaLogo";
import { InboxMock } from "@/components/InboxMock";
import { SocialProof } from "@/components/SocialProof";
import { PersonaSplit } from "@/components/PersonaSplit";
import { ProductTabs } from "@/components/ProductTabs";
import RoiCalculator from "@/components/RoiCalculator";

const PLAN_ORDER = ["starter", "growth", "scale"];
const HIGHLIGHTS = {
  starter: ["Up to 5 CRM users", "Weighted quarterly forecast", "Risk Radar + Funnel X-Ray", "Weekly AI briefing"],
  growth: ["Up to 15 CRM users", "Everything in Starter", "Team performance & quotas", "Priority sync"],
  scale: ["Up to 25 CRM users", "Everything in Growth", "Custom onboarding", "Dedicated CS contact"],
};

function groupByPlan(plans) {
  const bySlug = {};
  for (const p of plans) {
    bySlug[p.plan_slug] = bySlug[p.plan_slug] || {
      slug: p.plan_slug,
      label: p.label,
      seats_max: p.seats_max,
    };
    bySlug[p.plan_slug][p.interval] = p;
  }
  return PLAN_ORDER.map((s) => bySlug[s]).filter(Boolean);
}

const SAMPLE_BRIEFING = {
  headline: "€138,391",
  delta: "↓ €20,750",
  target: "€300,000",
  gap: "€41,609",
  why: "Aurora Health SaaS pilot renewal slipped a week and Northwind Analytics annual shrunk after a discount request.",
  actions: [
    { name: "Quantum Retail — Expansion", amount: "€26,500", stage: "Proposal", note: "No activity in 11 days. → Send a decision-timeline nudge today." },
    { name: "Aurora Health — SaaS renewal", amount: "€18,200", stage: "Negotiation", note: "Close date pushed twice. → Confirm legal-review path and lock a date." },
  ],
};

export default function LandingPage() {
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("month");
  const { user } = useAuth();
  const nav = useNavigate();

  // Logged-in visitors go to the app (dashboard/paywall gate decides where)
  useEffect(() => {
    if (user && user !== false) {
      nav("/dashboard", { replace: true });
    }
  }, [user, nav]);

  useEffect(() => {
    api
      .get("/billing/plans")
      .then(({ data }) => setPlans(groupByPlan(data.plans || [])))
      .catch(() => setPlans([]));
    // Fire-and-forget landing-view ping (aggregate only — no PII).
    api.post("/analytics/landing-view").catch(() => {});
  }, []);

  // Cal.com popup embed — lazy-load once, wire any element with
  // data-cal-link="piyush-recrysta/15min" to open the booking modal.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__calInited) return;
    window.__calInited = true;
    (function (C, A, L) {
      let p = function (a, ar) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () { p(api, arguments); };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");
    window.Cal("init", "founder-chat", { origin: "https://cal.com" });
    window.Cal.ns["founder-chat"]("ui", {
      theme: "light",
      styles: { branding: { brandColor: "#10b981" } },
      hideEventTypeDetails: false,
      layout: "month_view",
    });
  }, []);

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900" data-testid="landing-page">
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-950 to-slate-900 text-white">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 25% 30%, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        {/* Subtle emerald refraction glow behind headline */}
        <div className="absolute -left-40 top-32 h-96 w-96 bg-emerald-500/20 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left: headline + copy + CTA + stats */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-300/90">
                <span className="text-emerald-400/70">/</span> for_b2b_teams_of_5_to_25_reps
              </div>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02]" data-testid="landing-hero-title">
                Know what you&apos;ll
                <span className="block text-emerald-400">really close.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl" data-testid="landing-hero-sub">
                The AI analyst that sends your Monday briefing: the real forecast, the deals
                that need attention, and why the number moved.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-3">
                <Link
                  to="/signup"
                  data-testid="landing-hero-cta"
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 text-slate-950 px-5 py-3 text-sm font-bold hover:bg-emerald-400 transition-colors"
                >
                  Start free 14-day trial <ArrowRight className="h-4 w-4" />
                </Link>
                <button
                  type="button"
                  data-cal-namespace="founder-chat"
                  data-cal-link="piyush-recrysta/15min"
                  data-cal-config='{"layout":"month_view"}'
                  data-testid="landing-hero-founder-link"
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/60 bg-white/5 text-emerald-300 hover:text-white hover:bg-emerald-500/15 hover:border-emerald-400 px-4 py-3 text-sm font-semibold transition-colors"
                >
                  <CalendarClock className="h-4 w-4" />
                  Book a 15-min chat with the founder
                </button>
                <a
                  href="#pricing"
                  className="text-sm text-slate-300 hover:text-white underline underline-offset-4"
                  data-testid="landing-hero-pricing-link"
                >
                  See pricing
                </a>
              </div>
              <div className="mt-4 text-xs text-slate-400">
                No credit card required. Cancel any time during the trial.
              </div>

              {/* Stat callouts */}
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg" data-testid="landing-hero-stats">
                <HeroStat Icon={Clock} value="10 min" label="setup time" />
                <HeroStat Icon={Inbox} value="Mon 06:00" label="in your inbox" />
                <HeroStat Icon={Wallet} value="€99/mo" label="starts at" />
              </div>
            </div>

            {/* Right: animated inbox mock */}
            <div className="lg:col-span-5">
              <InboxMock />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison strip — quick positioning below hero */}
      <section className="bg-slate-950 border-b border-slate-800 py-10 text-center" data-testid="landing-comparison-strip">
        <p className="text-lg sm:text-xl font-medium text-slate-200 max-w-3xl mx-auto px-6">
          Not Clari. Not another dashboard. Just a <span className="text-emerald-400">weekly briefing</span> that tells you what you&rsquo;ll really close.
        </p>
        <a href="/vs-hubspot" className="mt-3 inline-block text-xs text-slate-400 hover:text-emerald-400 underline underline-offset-4" data-testid="landing-see-different">
          See how we&rsquo;re different →
        </a>
      </section>

      {/* Social proof (Phase B) — honest early-access positioning, no fake logos */}
      <SocialProof />

      {/* Pain points */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20" data-testid="landing-pains">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PainCard
            Icon={LineChart}
            title="Your CRM forecast is fantasy"
            body="HubSpot's default forecast is a manager guess dressed up as a number. It ignores stage conversion history and never asks whether the deal has moved this week."
          />
          <PainCard
            Icon={MoonStar}
            title="Deals die quietly"
            body="Nobody notices when a €40k negotiation goes silent for 10 days. By the time a rep flags it in the pipeline review, the buyer's already talking to someone else."
          />
          <PainCard
            Icon={CalendarClock}
            title="You find out at quarter-end"
            body="The story of what actually happened this quarter arrives in week 13 — after every lever you could have pulled is already gone."
          />
        </div>
      </section>

      {/* Persona split (Phase C) — three-way pitch to founder / revops / sales leader */}

      {/* Product preview: sample Monday briefing */}
      <section className="bg-white border-y border-slate-200" data-testid="landing-product-preview">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-700">
                <span className="text-emerald-500/70">/</span>the_briefing
              </div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
                One page. Zero fluff.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                A single briefing written by AI from your last 7 days of deal movement, health-scored,
                and shipped to your inbox before your first meeting. It names the deals, not the metrics.
              </p>
              <p className="mt-3 text-sm text-slate-500" data-testid="landing-briefing-schedule-note">
                Delivered Monday 06:00 — or daily, or any weekday you choose.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5" />
                  <span>Real week-over-week delta with the specific deals that moved the number</span>
                </li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5" />
                  <span>2–4 deals needing action today, each with a concrete next step</span>
                </li>
                <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 mt-0.5" />
                  <span>Funnel and bright-spot callouts, only when they&apos;re genuinely notable</span>
                </li>
              </ul>
            </div>
            <SampleBriefingCard />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20" data-testid="landing-how-it-works">
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
            <span className="text-emerald-500/70">/</span>how_it_works
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
            Live in 10 minutes. No implementation project.
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Step n={1} Icon={PlugZap} title="Connect HubSpot" body="One OAuth click. Read-only access to deals and owners." />
          <Step n={2} Icon={ListTree} title="Confirm your pipeline mapping" body="We pre-fill a mapping from your stage names. You review it in under 2 minutes." />
          <Step n={3} Icon={LineChart} title="Get your dashboard + Monday briefing" body="Forecast, Risk Radar, Funnel X-Ray, Team, Bookings — plus a Monday-morning AI briefing." />
        </div>
      </section>

      {/* Product tabs (Phase D) — animated micro-loops of each dashboard tab */}
      <PersonaSplit />

      <ProductTabs />

      {/* ROI calculator */}
      <RoiCalculator />

      {/* Mid-page CTA — after the ROI calc, offer the low-friction founder chat
          instead of yet another trial button (pricing sits directly below with its own). */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16 text-center" data-testid="landing-mid-cta">
        <div className="inline-flex flex-col items-center">
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-3">
            Still weighing it up?
          </div>
          <button
            type="button"
            data-cal-namespace="founder-chat"
            data-cal-link="piyush-recrysta/15min"
            data-cal-config='{"layout":"month_view"}'
            data-testid="landing-mid-cta-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-6 py-3.5 text-sm font-bold hover:bg-slate-800 ring-2 ring-emerald-400/40 hover:ring-emerald-400 transition-all"
          >
            <CalendarClock className="h-4 w-4" />
            Book a 15-min chat with the founder
          </button>
          <div className="mt-3 text-xs text-slate-500">
            Real person, real answers. No sales script, no obligation.
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-white border-y border-slate-200 py-20" data-testid="landing-pricing">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Pricing</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">Simple pricing.</h2>
            <p className="mt-3 text-slate-600">Every plan includes the full product. Pick the seat cap that fits your team.</p>
          </div>

          <div className="mt-8 flex justify-center">
            <div className="inline-flex items-center rounded-full bg-slate-100 p-1" data-testid="landing-interval-toggle">
              <button
                onClick={() => setInterval("month")}
                data-testid="landing-interval-monthly"
                className={`px-4 py-1.5 text-xs font-semibold rounded-full transition ${
                  interval === "month" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setInterval("year")}
                data-testid="landing-interval-yearly"
                className={`px-4 py-1.5 text-xs font-semibold rounded-full transition flex items-center gap-1.5 ${
                  interval === "year" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Annual
                <span className="text-[10px] uppercase tracking-wider bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">2 months free</span>
              </button>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="landing-pricing-grid">
            {plans.map((p) => {
              const price = p[interval];
              if (!price) return null;
              const featured = p.slug === "growth";
              const perMonth = interval === "year" ? Math.round(price.amount_eur / 12) : price.amount_eur;
              return (
                <div
                  key={p.slug}
                  data-testid={`landing-plan-${p.slug}`}
                  className={`rounded-2xl p-6 flex flex-col relative border ${
                    featured
                      ? "border-emerald-500 ring-2 ring-emerald-200 shadow-xl bg-white -mt-3"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  {featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 text-white text-[10px] font-bold tracking-wider uppercase px-3 py-1">
                      Most popular
                    </div>
                  )}
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">{p.label}</div>
                  <div className="mt-2 flex items-baseline gap-1.5">
                    <div className="text-4xl font-extrabold tracking-tight text-slate-900">
                      €{price.amount_eur.toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-500">/{interval === "year" ? "yr" : "mo"}</div>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {interval === "year" ? `€${perMonth.toLocaleString()} per month, billed yearly` : "billed monthly"}
                  </div>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {(HIGHLIGHTS[p.slug] || []).map((h) => (
                      <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/signup"
                    data-testid={`landing-plan-cta-${p.slug}`}
                    className={`mt-6 w-full inline-flex items-center justify-center rounded-lg py-2.5 text-sm font-semibold transition ${
                      featured ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-white border border-slate-300 text-slate-900 hover:bg-slate-100"
                    }`}
                  >
                    Start free 14-day trial
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-center text-xs text-slate-500">
            EUR-priced. VAT added automatically at checkout. Have a coupon? Enter it during Checkout.
          </div>
          <div className="mt-3 text-center text-sm text-slate-600" data-testid="landing-bigger-team">
            Bigger team?{" "}
            <a href="mailto:support@recrysta.com" className="text-slate-900 font-semibold underline underline-offset-4 hover:no-underline">
              Contact us →
            </a>
          </div>
        </div>
      </section>

      {/* Comparison strip */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20" data-testid="landing-comparison">
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500"><span className="text-emerald-500/70">/</span>alternatives</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">Why not just…</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <CompareCard
            title="Clari"
            price="~$160k/yr"
            body="Enterprise-only. Long implementation, dedicated CS, minimum seat counts you probably don't hit."
            tone="muted"
          />
          <CompareCard
            title="Spreadsheets"
            price="Free"
            body="Wrong by Tuesday. Owned by whoever last touched it, no history, no risk flags, no briefing."
            tone="muted"
          />
          <CompareCard
            title="Recrysta"
            price="From 99 EUR/mo"
            body="Live in 10 minutes. Built for 5–25 rep teams. Real forecast + Monday briefing, EU-hosted."
            tone="accent"
          />
        </div>
      </section>

      {/* Trust */}
      <section className="bg-slate-950 text-slate-100 py-20" data-testid="landing-trust">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">Trust & privacy</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">Your data stays yours.</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <TrustItem
              Icon={Server}
              title="EU data hosting"
              body="All storage in the European Union. HubSpot tokens encrypted at rest."
            />
            <TrustItem
              Icon={ShieldCheck}
              title="Your data is never used to train AI models"
              body="We use AI providers under enterprise no-training terms — nothing you send us is used to improve any model."
            />
            <TrustItem
              Icon={Lock}
              title="Minimal data sent to AI"
              body="The AI briefing sees pseudonymized metrics and deal names only — no contact emails, no company descriptions, no owner PII."
            />
            <TrustItem
              Icon={FileText}
              title="DPA available on request"
              body="Standard Data Processing Agreement covering GDPR Article 28 — email us and we'll send it over."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Ready for a Monday you actually understand?</h2>
          <p className="mt-4 text-slate-600">Connect in 10 minutes. Your first briefing lands this Monday.</p>
          <Link
            to="/signup"
            data-testid="landing-footer-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-5 py-3 text-sm font-bold hover:bg-slate-800 transition-colors"
          >
            Start free 14-day trial <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-6 text-sm text-slate-600 leading-relaxed" data-testid="landing-footer-founder-line">
            Prefer to talk first? I&apos;m the founder — reach me directly at{" "}
            <a
              href="mailto:support@recrysta.com?subject=Question%20about%20Recrysta"
              className="font-semibold text-slate-900 underline underline-offset-4 decoration-slate-300 hover:decoration-emerald-500"
              data-testid="landing-footer-founder-email"
            >
              support@recrysta.com
            </a>
            .
          </p>
        </div>
      </section>

      {/* About */}
      <section className="border-t border-slate-200 bg-slate-50 py-16" data-testid="landing-about">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500"><span className="text-emerald-500/70">/</span>about</div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
            An analyst for every business.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Analyst-grade revenue intelligence shouldn&apos;t require a data team or a
            six-figure contract. Recrysta starts with the revenue question that keeps
            founders awake — <span className="text-slate-900 font-medium">what will I really close this quarter?</span> — and expands to every number that matters.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TopNav() {
  return (
    <header className="sticky top-0 z-20 bg-slate-950/80 backdrop-blur border-b border-white/5" data-testid="landing-topnav">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-white">
          <RecrystaMark size={32} />
          <span className="text-sm font-bold tracking-tight">Recrysta</span>
        </Link>
        <div className="flex items-center gap-4">
          <a href="#pricing" className="hidden sm:inline text-sm text-slate-300 hover:text-white">Pricing</a>
          <Link to="/login" className="text-sm text-slate-300 hover:text-white" data-testid="landing-login-link">Log in</Link>
          <Link
            to="/signup"
            data-testid="landing-topnav-cta"
            className="rounded-lg bg-emerald-500 text-slate-950 px-3 py-1.5 text-xs font-bold hover:bg-emerald-400 transition-colors"
          >
            Start free trial
          </Link>
        </div>
      </div>
    </header>
  );
}

function HeroStat({ Icon, value, label }) {
  return (
    <div className="border-l-2 border-emerald-500/60 pl-3" data-testid="landing-hero-stat">
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-300/80">
        <Icon className="h-2.5 w-2.5" /> {label}
      </div>
      <div className="mt-1 text-lg font-bold text-white tracking-tight">{value}</div>
    </div>
  );
}

function PainCard({ Icon, title, body }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="landing-pain-card">
      <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-lg font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Step({ n, Icon, title, body }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`landing-step-${n}`}>
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-full bg-emerald-500 text-slate-950 font-bold text-sm flex items-center justify-center">
          {n}
        </div>
        <Icon className="h-5 w-5 text-slate-500" />
      </div>
      <h3 className="mt-4 text-lg font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function CompareCard({ title, price, body, tone }) {
  const isAccent = tone === "accent";
  return (
    <div
      className={`rounded-2xl p-6 ${isAccent ? "bg-slate-900 text-white ring-2 ring-emerald-500" : "bg-white border border-slate-200 text-slate-900"}`}
      data-testid="landing-compare-card"
    >
      <div className={`text-xs uppercase tracking-[0.18em] font-semibold ${isAccent ? "text-emerald-300" : "text-slate-500"}`}>
        {title}
      </div>
      <div className={`mt-2 text-2xl font-extrabold ${isAccent ? "text-white" : "text-slate-900"}`}>{price}</div>
      <p className={`mt-3 text-sm leading-relaxed ${isAccent ? "text-slate-300" : "text-slate-600"}`}>{body}</p>
    </div>
  );
}

function TrustItem({ Icon, title, body }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid="landing-trust-item">
      <div className="flex items-start gap-4">
        <Icon className="h-6 w-6 text-emerald-300 flex-shrink-0" />
        <div>
          <div className="text-base font-bold text-white">{title}</div>
          <div className="mt-1 text-sm text-slate-300 leading-relaxed">{body}</div>
        </div>
      </div>
    </div>
  );
}

function SampleBriefingCard() {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-xl overflow-hidden" data-testid="landing-sample-briefing">
      <div className="px-6 pt-5 pb-2 flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold inline-flex items-center gap-2">
          <RecrystaMark size={16} />
          Monday Briefing · Recrysta
        </div>
        <span className="text-[10px] uppercase tracking-wider bg-slate-900 text-white px-2 py-0.5 rounded-full font-bold">AI · Claude</span>
      </div>
      <div className="px-6 pt-2 pb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-4xl font-extrabold tracking-tight text-slate-900">{SAMPLE_BRIEFING.headline}</div>
          <span className="inline-block px-2.5 py-1 rounded-full bg-red-600 text-white text-xs font-bold">{SAMPLE_BRIEFING.delta}</span>
        </div>
        <div className="mt-4 grid grid-cols-2 divide-x divide-slate-200 border-t border-b border-slate-200 py-3">
          <div className="pr-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Target</div>
            <div className="mt-1 text-sm font-semibold text-slate-900 number-tabular">{SAMPLE_BRIEFING.target}</div>
          </div>
          <div className="pl-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Gap</div>
            <div className="mt-1 text-sm font-semibold text-slate-900 number-tabular">{SAMPLE_BRIEFING.gap}</div>
          </div>
        </div>
      </div>
      <div className="px-6 pb-4">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Why it moved</div>
        <div className="mt-2 text-sm text-slate-900 leading-relaxed">{SAMPLE_BRIEFING.why}</div>
      </div>
      <div className="px-6 pb-4">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Deals needing action today</div>
        <div className="mt-2">
          {SAMPLE_BRIEFING.actions.map((a, i) => (
            <div key={i} className={`py-3 ${i > 0 ? "border-t border-slate-200" : ""}`}>
              <div className="flex items-baseline justify-between gap-4">
                <div className="text-sm font-semibold text-slate-900 min-w-0 flex-1 break-words">{a.name}</div>
                <div className="text-sm font-semibold text-slate-900 number-tabular whitespace-nowrap flex-shrink-0">
                  {a.amount}
                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-slate-900 text-white px-1.5 py-0.5 rounded-full">{a.stage}</span>
                </div>
              </div>
              <div className="mt-1 text-sm text-slate-600">{a.note}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="px-6 py-3 border-t border-slate-100 text-xs text-slate-500">Illustrative example. Not a real customer.</div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white" data-testid="landing-footer">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-2 text-slate-600">
          <RecrystaMark size={28} />
          <span className="text-sm font-bold text-slate-900">Recrysta</span>
          <span className="text-xs text-slate-400">· Know what you&apos;ll really close. · EU hosting</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <a href="mailto:support@recrysta.com" className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5" /> support@recrysta.com
          </a>
          <Link to="/privacy" className="text-slate-600 hover:text-slate-900" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/imprint" className="text-slate-600 hover:text-slate-900" data-testid="footer-imprint-link">Imprint</Link>
          <Link to="/faq" className="text-slate-600 hover:text-slate-900" data-testid="footer-faq-link">FAQ</Link>
          <Link to="/vs-hubspot" className="text-slate-600 hover:text-slate-900" data-testid="footer-vs-hubspot-link">Recrysta vs HubSpot</Link>
        </div>
      </div>
      <div className="border-t border-slate-100 py-4 text-center text-xs text-slate-400">
        © {new Date().getFullYear()} Recrysta. All rights reserved.
      </div>
    </footer>
  );
}

